import { useState, useEffect } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { Button } from "../components/ui/button";
import { MarkdownCell, CodeCell, Stream } from "../components/Cell";
import { SpeciesSearchBox } from "../components/SpeciesSearchBox";
import { GbifPanel } from "../components/GbifPanel";
import { InatGrid } from "../components/InatGrid";
import { PhotoGrid, ExtLink } from "../components/PhotoGrid";
import { CountrySelect } from "../components/CountrySelect";
import { ExportBar } from "../components/ExportBar";
import { PhotoMatch } from "../components/PhotoMatch";
import { lookupSpecies, errMsg } from "../lib/api";
import { countryName } from "../lib/countries";
import { speciesNotebook, speciesCsv, exportNotebook, exportCsv } from "../lib/exporters";

const strip = (h) => (h || "").replace(/<[^>]+>/g, "");

export default function SpeciesSearchPage({ goUpload, query, setKernel }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [country, setCountry] = useState(null);
  const [refId, setRefId] = useState(null);
  const [scores, setScores] = useState(null);

  const search = async (n, c = country) => {
    setName(n); setLoading(true); setD(null); setScores(null); setKernel("busy");
    try { setD(await lookupSpecies(n, c, countryName(c))); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); setKernel("idle"); }
  };
  useEffect(() => { if (query?.name) { if (query.refId) setRefId(query.refId); search(query.name); } }, [query]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeCountry = (c) => { setCountry(c); if (name) search(name, c); };

  const g = d?.gbif, i = d?.inaturalist;
  const gFound = g?.usageKey && g.matchType !== "HIGHERRANK";
  const cArg = d?.region?.country ? `, country="${d.region.country}"` : "";
  return (
    <div>
      <MarkdownCell>
        <h1>Species explorer</h1>
        <p>Search any moth / butterfly name (scientific or common). Results come live from the <code>GBIF Backbone Taxonomy</code> and <code>iNaturalist</code>. Only exact name matches are shown, so the images belong to that species.</p>
        <Button data-testid="search-go-upload-button" size="sm" variant="outline" onClick={goUpload}><UploadCloud className="h-4 w-4 mr-1.5" />Upload a moth photo to identify</Button>
      </MarkdownCell>
      <div className="relative z-30"><CodeCell n={1} running={loading} testid="species-search-cell" code={`name = input("species name: ")${name ? `   # "${name}"` : ""}\ncountry = ${country ? `"${country}"` : "None"}   # region filter (ISO-2)\nsp = search_species(name, country=country)`}>
        <div className="flex flex-col lg:flex-row lg:items-start gap-3">
          <div className="flex-1"><SpeciesSearchBox onSearch={search} loading={loading} initial={name} /></div>
          <CountrySelect value={country} onChange={changeCountry} />
        </div>
      </CodeCell></div>
      {d && (
        <div data-testid="species-result">
          <CodeCell n={2} testid="species-summary-cell" code={"sp.summary()"}>
            <Stream testid="species-summary">{`query            : "${d.query}"
region           : ${d.region ? `${d.region.country_name} (${d.region.country})` : "worldwide"}
GBIF match       : ${g?.matchType}${gFound ? ` → ${g.scientificName} (usageKey=${g.usageKey}, ${g.status})` : ""}
iNaturalist      : ${i?.found ? `${i.name} · ${i.common_name || ""} (taxon_id=${i.taxon_id})` : "not found (exact name)"}
rank             : ${(g?.rank || i?.rank || "").toLowerCase()}
names agree      : ${d.taxonomic_agreement}`}</Stream>
            {gFound && g.rank && g.rank !== "SPECIES" && <Stream kind="stderr" testid="species-rank-note">{`Note: "${d.query}" is a ${g.rank.toLowerCase()} — images below may show several species within it (each image is labelled).`}</Stream>}
            {!gFound && !i?.found && <Stream kind="stderr" testid="species-not-found">{`SpeciesNotFound: "${d.query}" not found in GBIF or iNaturalist (Lepidoptera). Try the scientific name or pick a suggestion.`}</Stream>}
            {i?.found && i.wikipedia_summary && <p data-testid="species-wiki-summary" className="text-sm text-foreground/85 max-w-3xl px-3 mt-1">{strip(i.wikipedia_summary)} {i.wikipedia_url && <ExtLink href={i.wikipedia_url}>Wikipedia</ExtLink>}</p>}
            {(gFound || i?.found) && <div className="px-3 mt-3"><ExportBar prefix="species" onNotebook={() => exportNotebook(`species_${d.query}${d.region ? "_" + d.region.country : ""}`, speciesNotebook(d))} onCsv={() => exportCsv(`species_${d.query}${d.region ? "_" + d.region.country : ""}`, speciesCsv(d))} /></div>}
          </CodeCell>
          {(gFound || i?.found) && (
            <>
              <MarkdownCell><h2>Which photos match my moth?</h2><p>Pick one of your uploaded moths; the AI scores how closely each GBIF / iNaturalist photo below matches it. The top 3 get a green frame.</p></MarkdownCell>
              <CodeCell n={3} testid="photo-match-cell" code={'scores = model.match_photos(my_photo, gbif_media + inat_photos)\nscores.sort_values("score", ascending=False).head(3)'}>
                <PhotoMatch d={d} refId={refId} setRefId={setRefId} scores={scores} setScores={setScores} setKernel={setKernel} />
              </CodeCell>
            </>
          )}
          <MarkdownCell><h2>GBIF</h2></MarkdownCell>
          <CodeCell n={4} testid="species-gbif-cell" code={`gbif = requests.get(f"{GBIF}/species/match", params={"name": "${d.query}"}).json()\nshow_taxonomy(gbif, occ${cArg})`}>
            {gFound ? <GbifPanel gbif={g} country={d.region?.country} bbox={d.region?.bbox} /> : <Stream kind="stderr" testid="species-gbif-none">{`GBIF: no species-level match (matchType=${g?.matchType})`}</Stream>}
          </CodeCell>
          {gFound && (
            <CodeCell n={5} testid="species-gbif-media-cell" code={`media = requests.get(f"{GBIF}/occurrence/search", params={"taxonKey": ${g.acceptedUsageKey || g.usageKey}, "mediaType": "StillImage"${d.region ? `, "country": "${d.region.country}"` : ""}}).json()`}>
              <PhotoGrid items={g.media} scores={scores} testid="gbif-media-grid" empty={d.region ? `No GBIF occurrence images from ${d.region.country_name}.` : "No GBIF occurrence images for this taxon."} />
            </CodeCell>
          )}
          <MarkdownCell><h2>iNaturalist</h2></MarkdownCell>
          {i?.found ? (
            <>
              <CodeCell n={6} testid="species-inat-photos-cell" code={`taxon = requests.get(f"{INAT}/taxa/${i.taxon_id}").json()["results"][0]\ntaxon["taxon_photos"]`}>
                <PhotoGrid items={i.taxon_photos.map((p) => ({ ...p, url: i.url, species: i.name }))} scores={scores} testid="inat-taxon-photos-grid" empty="No curated taxon photos." />
              </CodeCell>
              <CodeCell n={7} testid="species-inat-obs-cell" code={`obs = requests.get(f"{INAT}/observations", params={"taxon_id": ${i.taxon_id}, "quality_grade": "research"${d.region?.inat_place_id ? `, "place_id": ${d.region.inat_place_id}` : ""}}).json()`}>
                <InatGrid inat={i} scores={scores} region={d.region} />
              </CodeCell>
            </>
          ) : <CodeCell n={6} code={`requests.get(f"{INAT}/taxa", params={"q": "${d.query}"})`}><Stream kind="stderr" testid="species-inat-none">iNaturalist: no exact Lepidoptera taxon match</Stream></CodeCell>}
        </div>
      )}
    </div>
  );
}
